/**
 * @file scroll.ts
 * @description Прокрутка панели к группе настроек (phase19.16, SB122;
 * uiux-settings-groups §2): ближайший прокручиваемый предок панели, высота
 * липкой строки сверху и сдвиг к заголовку группы под неё.
 *
 * Прокручиваем только свой контейнер (`scrollTo`), а не `scrollIntoView`:
 * страница на 360 по горизонтали не сдвигается (урок OrderChainStepper).
 */

/** Запас между липкой строкой и заголовком группы, px. */
export const STICKY_GAP = 8;

/** Ближайший предок с вертикальной прокруткой (`overflow-y: auto|scroll`); нет — null (окно). */
export function findScrollParent(el: HTMLElement | null): HTMLElement | null {
  let node = el?.parentElement ?? null;
  while (node && node !== document.body && node !== document.documentElement) {
    const overflow = window.getComputedStyle(node).overflowY;
    if (overflow === "auto" || overflow === "scroll") return node;
    node = node.parentElement;
  }
  return null;
}

/** Плавно, если человек не просил меньше движения. */
export function scrollBehavior(): ScrollBehavior {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";
  } catch {
    return "auto";
  }
}

/**
 * Прокрутить контейнер так, чтобы верх группы встал под липкую строку.
 * `onDone` — по окончании прокрутки (`scrollend` или запасной таймер).
 * Возвращает отмену: снять слушатель и таймер без `onDone` (панель закрыли).
 */
export function scrollGroupIntoView(group: HTMLElement, stickyHeight: number, onDone: () => void): () => void {
  const container = findScrollParent(group);
  const behavior = scrollBehavior();
  if (!container) {
    group.scrollIntoView?.({ block: "start", inline: "nearest", behavior });
    const timer = window.setTimeout(onDone, 700);
    return () => window.clearTimeout(timer);
  }
  const top = group.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop - stickyHeight - STICKY_GAP;
  let finished = false;
  const stop = () => {
    finished = true;
    container.removeEventListener("scrollend", done);
    window.clearTimeout(timer);
  };
  function done() {
    if (finished) return;
    stop();
    onDone();
  }
  // Запасной таймер: `scrollend` есть не везде, а без сдвига его не будет вовсе.
  const timer = window.setTimeout(done, behavior === "smooth" ? 900 : 50);
  container.addEventListener("scrollend", done);
  if (typeof container.scrollTo === "function") container.scrollTo({ top: Math.max(0, top), behavior });
  else container.scrollTop = Math.max(0, top);
  return stop;
}
